import React, { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./lib/auth";
import "./lib/i18n";
import MobileTabBar from "./components/MobileTabBar";
import MobileTopBar from "./components/MobileTopBar";
import SplashScreen from "./components/SplashScreen";
import ErrorBoundary from "./components/ErrorBoundary";
import { BookingCountProvider } from "./lib/BookingCountContext";

import Landing from "./pages/Landing";
import Services from "./pages/Services";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import UserDashboard from "./pages/UserDashboard";
import WorkerDashboard from "./pages/WorkerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCMS from "./pages/AdminCMS";
import BookService from "./pages/BookService";
import AuthCallback from "./pages/AuthCallback";
import PolicyPages from "./pages/PolicyPages";
import ProtectedRoute from "./components/ProtectedRoute";

const TAB_BAR_ROUTES = ["/", "/services", "/about", "/contact", "/dashboard", "/worker", "/admin"];

function AppRouter() {
  const loc = useLocation();
  // Handle Google OAuth callback synchronously to prevent race conditions
  if (loc.hash && loc.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  const showTabs = TAB_BAR_ROUTES.includes(loc.pathname);
  return (
    <BookingCountProvider>
      <div className="app-router">
        {showTabs && <MobileTopBar />}
        <div className={`page-content ${showTabs ? "has-tab-bar" : ""}`}>
          <Routes>
            <Route path="/" element={<Landing/>}/>
            <Route path="/services" element={<Services/>}/>
            <Route path="/about" element={<About/>}/>
            <Route path="/contact" element={<Contact/>}/>
            <Route path="/login" element={<Login/>}/>
            <Route path="/signup" element={<Signup/>}/>
            <Route path="/auth/callback" element={<AuthCallback/>}/>
            <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["user"]}><UserDashboard/></ProtectedRoute>}/>
            <Route path="/worker" element={<ProtectedRoute allowedRoles={["worker"]}><WorkerDashboard/></ProtectedRoute>}/>
            <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard/></ProtectedRoute>}/>
            <Route path="/admin/cms" element={<ProtectedRoute allowedRoles={["admin"]}><AdminCMS/></ProtectedRoute>}/>
            <Route path="/book/:serviceId" element={<ProtectedRoute allowedRoles={["user"]}><BookService/></ProtectedRoute>}/>
            <Route path="/page/:page" element={<PolicyPages/>}/>
            <Route path="*" element={
              <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6] gap-4">
                <h1 className="font-display text-6xl font-bold text-[#1C4532]">404</h1>
                <p className="text-[#646964] text-lg">Page not found</p>
                <a href="/" className="text-sm text-[#D9734E] hover:underline">Go home →</a>
              </div>
            }/>
          </Routes>
        </div>
        {showTabs && <MobileTabBar />}
      </div>
    </BookingCountProvider>
  );
}

function App() {
  const [splashDone, setSplashDone] = useState(false);

  return (
    <div className="App">
      {!splashDone && <SplashScreen onFinish={() => setSplashDone(true)} />}
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors closeButton/>
            <ErrorBoundary>
              <AppRouter />
            </ErrorBoundary>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
